import { useTranslate } from "@similarweb/i18n-provider";
import { Keywords, Star, Users } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { useTracking } from "@similarweb/tracking-provider";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useShouldPromoteExtension } from "app/custom-hooks/useShouldPromoteExtension";
import { AssetsService } from "app/services/AssetsService";
import {
    extensionPromotionSourceMap,
    getSimilarwebExtensionStoreUrl,
} from "app/UtilitiesAndConstants/UtilityFunctions/extensionStoreUrl";

import { BannerCard, BannerIllustration } from "./styles";

const BULLETS = [
    { key: "aftertrial.homepage.extension.banner.ratings", Icon: Star },
    { key: "aftertrial.homepage.extension.banner.users", Icon: Users },
    { key: "aftertrial.homepage.extension.banner.tools", Icon: Keywords },
] as const;

export const ExtensionAfterTrialBanner = () => {
    const { translate } = useTranslate();
    const { track } = useTracking();
    const shouldPromote = useShouldPromoteExtension();

    if (!shouldPromote) {
        return null;
    }

    const handleClick = () => {
        track("Internal Link", "click", "Extension/After trial Home page");
        window.open(
            getSimilarwebExtensionStoreUrl(extensionPromotionSourceMap.afterTrialHomePage),
            "_blank",
            "noopener,noreferrer",
        );
    };

    return (
        <BannerCard
            justifyContent="space-between"
            gap="24px"
            data-automation="extension-after-trial-banner"
        >
            <Stack gap="20px">
                <Stack gap="20px">
                    <Text
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate("aftertrial.homepage.extension.banner.title")}
                    </Text>
                    <Text
                        fontVariant="h6"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("aftertrial.homepage.extension.banner.description")}
                    </Text>
                </Stack>
                <Stack gap="12px">
                    {BULLETS.map(({ key, Icon }) => (
                        <Flex
                            key={key}
                            alignItems="center"
                            gap="8px"
                        >
                            <Icon
                                size="sm"
                                color={colorsPalettes.blue[400]}
                            />
                            <Text
                                fontVariant="input2"
                                color={colorsPalettes.carbon[500]}
                            >
                                {translate(key)}
                            </Text>
                        </Flex>
                    ))}
                </Stack>
                <IconButton
                    type="primary"
                    iconName="open-new-window"
                    iconSize="12px"
                    placement="right"
                    onClick={handleClick}
                    data-automation="extension-after-trial-banner-cta"
                    width="213px"
                >
                    {translate("aftertrial.homepage.extension.banner.cta")}
                </IconButton>
            </Stack>
            <BannerIllustration
                src={AssetsService.assetUrl("/images/activation/extension-after-trial-banner.png")}
                alt="after-trial-promo-banner"
            />
        </BannerCard>
    );
};
